import { JSX, useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { MapCard, isMapEmbedUrl } from './MapCard';
import { ChatStreamingContext, ChatTable } from './ChatTable';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

/**
 * What the assistant is allowed to emit as HTML.
 *
 * The reply is model-written text rendered into the admin, so raw HTML is passed through
 * rehypeRaw and then hard-filtered by rehypeSanitize against GitHub's schema. The order is not
 * optional: sanitising has to happen after the raw HTML has been parsed into nodes, or it has
 * nothing to inspect. The only additions are the class names the syntax highlighter needs, which
 * is why `className` is allowed on `code` for language-* and nowhere else useful to an attacker.
 */
const markdownSchema = {
    ...defaultSchema,
    attributes: {
        ...defaultSchema.attributes,
        code: [
            ...(defaultSchema.attributes?.code ?? []),
            ['className', /^language-./],
        ],
    },
};

/**
 * Follow a link without leaving the admin where that is possible.
 *
 * Records the assistant cites are HQ pages, and HQ is a hash-routed single page application, so
 * opening one in a new tab would reload the whole admin to show a record that is one route away.
 * Anything genuinely external still opens in a new tab.
 */
function ChatLink(props: any): JSX.Element {
    const href: string = props.href ?? '';

    // An OpenStreetMap embed becomes a map preview. Anything else, including the plain
    // openstreetmap.org search link used when an address could not be placed, stays a link.
    if (isMapEmbedUrl(href)) {
        return <MapCard href={ href } label={ String(props.children ?? 'View on map') } />;
    }

    function handleClick(event: React.MouseEvent<HTMLAnchorElement>): void {
        try {
            const target: URL = new URL(href, window.location.href);
            if (target.origin === window.location.origin && target.hash !== '') {
                event.preventDefault();
                window.location.hash = target.hash;
            }
        } catch {
            // A href that will not parse is left to the browser to refuse.
        }
    }

    return (
        <a href={href} target="_blank" rel="noreferrer" onClick={handleClick}>
            { props.children }
        </a>
    );
}

function CodeBlock({ className, children, ...rest }: any): JSX.Element {
    const [copied, setCopied] = useState<boolean>(false);
    const match: RegExpExecArray | null = /language-(\w+)/.exec(className ?? '');
    const source: string = String(children).replace(/\n$/, '');

    if (match === null) {
        return <code className={ className } { ...rest }>{ children }</code>;
    }

    function handleCopy(): void {
        navigator.clipboard?.writeText(source).then((): void => {
            setCopied(true);
            setTimeout((): void => setCopied(false), 1500);
        }).catch((): void => undefined);
    }

    return (
        <div className="message__code">
            <button className="code__copy" onClick={ handleCopy } type="button">
                { copied ? 'Copied' : 'Copy' }
            </button>
            <SyntaxHighlighter style={ oneDark } language={ match[1] } PreTag="div">
                { source }
            </SyntaxHighlighter>
        </div>
    );
}

/**
 * `streaming` is published rather than passed down because the table override is reached through
 * react-markdown, which decides what to render and gets no say from here.
 */
export const MarkdownMessage: (props: { content: string, streaming?: boolean }) => JSX.Element =
    ({ content, streaming }): JSX.Element => (
        <ChatStreamingContext.Provider value={ streaming === true }>
            <Markdown
                remarkPlugins={ [remarkGfm] }
                rehypePlugins={ [rehypeRaw, [rehypeSanitize, markdownSchema]] }
                components={ { a: ChatLink, code: CodeBlock, table: ChatTable } }
            >
                { content }
            </Markdown>
        </ChatStreamingContext.Provider>
    );
